import { Component } from "@theme/component";
import { fetchConfig, onAnimationEnd, preloadImage } from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  VariantUpdateEvent,
  VariantStockValidationEvent,
} from "@theme/events";
// import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @property {HTMLScriptElement} variantStockMapping - The script element containing variant stock mapping.
 * @property {HTMLButtonElement} viewProductButton - Redirects the user to the PDP for the current variant. used on non-pdp layouts for product card grids in conjunction with custom stock validation logic
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton", "variantStockMapping", "viewProductButton"];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
    // Indicates if the component has foribly updated the disabled/enabled button state via our custom stock logic
    // This is used to determine whether we should swap text or re-enable the button on variant changes or not, as we don't want to override Shopify's default stock handling unless we've had to step in for edge cases
    // This should also conviently get reset on and SSR reinjections of the component and allow the logic to re-run as required on a new state
    this.hasTouchedStock = false;
    this.validateStock();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Redirects the user to the PDP for the current variant.
   * @param {Event} event - The click event.
   */
  viewProduct(event) {
    event?.preventDefault();
    const url = this.refs.viewProductButton.dataset.productUrl;
    if(!url) return;
    window.location.href = url;
  }

  /**
   * Validates stock between T/NT variants due to inventory syncing delays.
   */
  validateStock() {
    // No need to do anything and trip our flags if Shopify has already disabled this. The button may be in a text state other than "Add to Cart" or "Unavailable" so we don't want to force on own to overwrite it.
    if(this.refs.addToCartButton.disabled && !this.hasTouchedStock) return;

    const currentVariantId = this.dataset.currentVariant;
    const variantStockMapping = JSON.parse(this.refs.variantStockMapping.textContent || "{}");
    if(!variantStockMapping || !currentVariantId) return;
    const currentVariantData = variantStockMapping[currentVariantId];
    if(!currentVariantData) return;

    const currentTaxBreak = currentVariantData.tax_break && currentVariantData.tax_break.length ? currentVariantData.tax_break[0] : {};
    const variantPairingID = currentTaxBreak.tax_swap_variant_id || null;
    if(!variantPairingID) return;

    const pairedVariantData = variantStockMapping[variantPairingID];
    if(!pairedVariantData) return;

    // Defaulting to true 
    // This is probably more of a safe-fail for any incorrect logic catches. Liquid will handle the Shopify stock by default and only miss this edge case fix.
    let canAddToCart = true;
    if(currentVariantData.available === false || pairedVariantData.available === false) {
      canAddToCart = false;
    }
    if(currentVariantData.inventory_quantity === 0 || pairedVariantData.inventory_quantity === 0) {
      canAddToCart = false;
    }

    /**
     * On product cards we can likely assume that this is rendered in a collection/grid based layout and not the PDP
     * Instead of displaying unavailable we should redirect them to the PDP for the possibility another variant is in stock
     * 
     */
    const isProductCard = this.closest("product-card") ? true : false;

    if(!canAddToCart) {
      this.hasTouchedStock = true;
      this.disable();
      this.setDisabledText();
      if(isProductCard) this.enableViewProductButton();
      this.dispatchEvent(
        new VariantStockValidationEvent({ variantId: currentVariantId, swapVariantId: variantPairingID, available: canAddToCart, source: "add-to-cart-component", sourceId: this.dataset.sectionId || null })
      );
      return;
    }
    
    if(canAddToCart && this.hasTouchedStock) {
      this.enable();
      this.setEnabledText();
      if(isProductCard) this.disableViewProductButton();
      this.dispatchEvent(
        new VariantStockValidationEvent({ variantId: currentVariantId, swapVariantId: variantPairingID, available: canAddToCart, source: "add-to-cart-component", sourceId: this.dataset.sectionId || null })
      );
    }
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * 
   * Updates the add to cart button and icon for our custom stock logic
   */ 
  setDisabledText() {
    const addToCartText = this.refs.addToCartButton.querySelector(".add-to-cart-text .add-to-cart-text__content");
    const addToCartIcon = this.refs.addToCartButton.querySelector(".add-to-cart-text .add-to-cart-icon");
    if(!addToCartText) return;
    // @ts-ignore
    addToCartText.textContent = window.translationStrings ? window.translationStrings.unavailable : "Unavailable";
    addToCartIcon?.classList.add("hidden");
  }

  /**
   * Hides the "view product" button for custom validated stock logic and re-displays the add to cart button on product cards
   */
  disableViewProductButton() {
    this.refs.viewProductButton.classList.add("hidden");
    this.refs.viewProductButton.setAttribute("aria-hidden", "true");
    this.refs.addToCartButton.classList.remove("hidden");
    this.refs.addToCartButton.removeAttribute("aria-hidden");
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * 
   * Updates the add to cart button and icon for our custom stock logic
   */ 
  setEnabledText() {
    const addToCartText = this.refs.addToCartButton.querySelector(".add-to-cart-text .add-to-cart-text__content");
    const addToCartIcon = this.refs.addToCartButton.querySelector(".add-to-cart-text .add-to-cart-icon");
    if(!addToCartText) return;
    // @ts-ignore
    addToCartText.textContent = window.translationStrings ? window.translationStrings.add_to_cart : "Add to cart";
    addToCartIcon?.classList.remove("hidden");
  }

  /**
   * Shows the "view product" button for custom validated stock logic and hides the add to cart button on product cards
   */
  enableViewProductButton() {
    this.refs.viewProductButton.classList.remove("hidden");
    this.refs.viewProductButton.removeAttribute("aria-hidden");
    this.refs.addToCartButton.classList.add("hidden");
    this.refs.addToCartButton.setAttribute("aria-hidden", "true");
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest("form");
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === "true";

    if (animationEnabled && !event.target.closest(".quick-add-modal")) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.add("atc-added");
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove("atc-added");
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * @typedef {Object} CartItem
 * @property {number} variant_id
 * @property {number} quantity
 * @property {string} [key]
 * @property {string} [title]
 * @property {string} [handle]
 * @property {string} [url]
 * @property {string} [image]
 * @property {number} [price]
 * @property {number} [final_price]
 * @property {number} [line_price]
 * @property {boolean} [requires_shipping]
 * @property {boolean} [gift_card]
 * @property {boolean} [taxable]
 * @property {Object.<string, any>} [properties]
 */

/**
 * @typedef {Object} Cart
 * @property {CartItem[]} items
 * @property {number} [item_count]
 * @property {number} [total_price]
 * @property {number} [total_discount]
 * @property {number} [total_weight]
 * @property {boolean} [requires_shipping]
 * @property {boolean} [currency]
 * @property {string} [note]
 * @property {string} [attributes]
 * @property {number} [original_total_price]
 * @property {number} [cart_level_discount_applications]
 * @property {number} [status]
 * @property {string} [message]
 * @property {string} [description]
 * @property {any} [errors]
 */


/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion", "zeroRatedConfig"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /****
   * Zero rated tax support variables
   */

  /** @type {boolean} */
  #zeroRatedSupport = false;
  /** @type {{ tax_break_qty?: number, tax_swap_variant_id?: number, taxable?: boolean } | null} */
  #zeroRatedConfig = null;
  /** @type {number | null} */
  #zeroRatedSwapVariant = null;
  /** @type {number | null | undefined} */
  #zeroRatedSwapQuantity = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );

    this.#setZeroRateConfig();
  }

  #setZeroRateConfig() {
    // ref [element] can be an array if multiple elements have the same ref name in the base class as they use querySelectorAll
    // Not exactly a case we expect here but unless we handle it we get type errors
    // We only expect one element with this ref name so we take the first element if it's an array
    // We also expect the element to be a script tag so we can read its textContent and parse it as JSON
    // If the element doesn't exist or doesn't have textContent, we set the config to null
    const zeroRatedConfigElement = Array.isArray(this.refs.zeroRatedConfig)
      ? this.refs.zeroRatedConfig[0]
      : this.refs.zeroRatedConfig;
    // Intentionally loose comparison to catch null and undefined and empty string - liquid still tends to output spaces when empty even with the `-` flag and metafield definitions are never strict types
    this.#zeroRatedConfig =
      zeroRatedConfigElement?.textContent &&
      zeroRatedConfigElement?.textContent != null
        ? JSON.parse(zeroRatedConfigElement.textContent)
        : null;
    if (Array.isArray(this.#zeroRatedConfig)) {
      this.#zeroRatedConfig = this.#zeroRatedConfig[0];
    }
    // Boolean if the product supports zero rated variants or not based on the above
    this.#zeroRatedSupport = this.#zeroRatedConfig ? true : false;

    // this.#zeroRatedConfig.tax_break_qty needs to be explicitly checked for isNaN as we have a unique 0 quantity support condition
    if (
      !this.#zeroRatedConfig ||
      !this.#zeroRatedConfig.tax_swap_variant_id
    )
      return;

    // The variant we swap to when quantity thresholds are met/unmet
    this.#zeroRatedSwapVariant = this.#zeroRatedConfig.tax_swap_variant_id;
    // The quantity threshold at which we swap to the zero rated swap variant and back
    this.#zeroRatedSwapQuantity = this.#zeroRatedConfig.tax_break_qty;
    if(this.#zeroRatedSwapQuantity !== undefined && isNaN(this.#zeroRatedSwapQuantity)) {
      this.#zeroRatedSwapQuantity = null;
      return;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the error conditions for the product form. This is split out to handle multiple logic flows.
   *
   * @param {{ message: string, description: string, errors?: any }} response - The error response from the server.
   * @param {HTMLFormElement} form - The form element that triggered the error.
   * @param {FormData} formData - The form data that was submitted.
   */
  async handleCartError(form, formData, response) {
    const { addToCartTextError } = this.refs;
    this.dispatchEvent(
      new CartErrorEvent(
        form.getAttribute("id") || "",
        response.message,
        response.description || "",
        response.errors
      )
    );

    if (!addToCartTextError) return;
    addToCartTextError.classList.remove("hidden");

    // Reuse the text node if the user is spam-clicking
    const textNode = addToCartTextError.childNodes[2];
    if (textNode) {
      textNode.textContent = response.message;
    } else {
      const newTextNode = document.createTextNode(response.message);
      addToCartTextError.appendChild(newTextNode);
    }

    // Create or get existing error live region for screen readers
    this.#setLiveRegionText(response.message);

    this.#timeout = setTimeout(() => {
      if (!addToCartTextError) return;
      addToCartTextError.classList.add("hidden");

      // Clear the announcement
      this.#clearLiveRegionText();
    }, 10000);

    this.refs.addToCartButtonContainer?.refs.addToCartButton?.removeAttribute("disabled");

    // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
    // because our back-end still adds the max allowed amount to the cart.
    // Fetch the actual cart to get the real item count rather than using the requested quantity,
    // which would incorrectly inflate the cart icon bubble count.
    let actualItemCount = 0;
    try {
      const cartResponse = await fetch(Theme.routes.cart_url, fetchConfig("javascript"));
      if (cartResponse.ok) {
        const cart = await cartResponse.json();
        actualItemCount = cart.item_count ?? 0;
      }
    } catch (_) {
      // If we can't fetch the cart, fall back to 0 to avoid inflating the count
    }

    this.dispatchEvent(
      new CartAddEvent({}, this.id, {
        didError: true,
        source: 'product-form-error',
        itemCount: actualItemCount,
        productId: this.dataset.productId,
      })
    );
  }

  /**
   * Handles the final submission request to the server for the product form. This is split out to handle multiple logic flows.
   * @param {HTMLFormElement} form - The form element.
   * @param {FormData} formData - The form data to submit.
   * @param {Event} event - The submit event.
   * @param {Boolean} skipLineCheck - Skips checking the current cart items. Zero rated products have already done this check and so we don't need the duplicate call
   */
  async handleFinalSubmission(form, formData, event, skipLineCheck = false) {
    const { addToCartTextError } = this.refs;
    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component"
    );
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    });

    // Find current LIP indexes in cart
    let CustomLIPIndex = null;
    let temporaryIndex = -1;
    if(skipLineCheck === false) {
      const currentCartFetchConfig = fetchConfig("javascript");
      let currentCartRequest = await fetch(Theme.routes.cart_url, {
        ...currentCartFetchConfig,
      });
      if (!currentCartRequest.ok) {
        console.error("Unable to update cart", currentCartRequest);
        this.handleCartError(form, formData, { message: "Unable to update cart", description: "Unable to fetch current cart" });
        this.refs.addToCartButtonContainer?.refs.addToCartButton?.removeAttribute("disabled");
        return;
      }
      const currentCart = await currentCartRequest.json();
      if(currentCart.items.length) {
        const currentVariantInCart = /** @type {Cart} */(currentCart).items.find(
          (item) => item.variant_id === Number(formData.get("id"))
        );
        if(currentVariantInCart && currentVariantInCart.properties && currentVariantInCart.properties["_customSortingIndex"]) {
          CustomLIPIndex = parseInt(currentVariantInCart.properties["_customSortingIndex"], 10);
        }
        else {
          const maxIndex = currentCart.items.reduce((/** @type {number} */ max, /** @type {CartItem} */ item) => {
              const index = parseInt(item.properties?._customSortingIndex, 10);
              return index > max ? index : max;
            }, /** @type {number} */ (-1));
          temporaryIndex = maxIndex + 1;
        }
      }
      else {
        CustomLIPIndex = 0;
      }
      
      if(temporaryIndex > -1) {
        CustomLIPIndex = temporaryIndex;
      }
      formData.set("properties[_customSortingIndex]", String(CustomLIPIndex));
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleCartError(form, formData, response);
          return;
        } else {
          const id = formData.get("id");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton =
              this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector(
              ".add-to-cart-text--added"
            );
            const addedText =
              addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        this.refs.addToCartButtonContainer?.refs.addToCartButton?.removeAttribute("disabled");
        //cartPerformance.measureFromEvent("add:user-action", event);

      });
  }

  async getCurrentVariantStock(variantId) {
    if(!variantId) {
      console.error("Variant ID is required to get current stock");
      return null;
    }
    const domain = window.Shopify?.shop || "";
    if(!domain) {
      console.error("Shop domain is required to get current stock");
      return null;
    }

    const graphUrl = `https://${domain}/api/2025-01/graphql.json`;
    const token = '747f691903c5c994f1e2f938559f1bd7';
    const query = `query VariantInventory($id: ID!) { node(id: $id) { __typename ... on ProductVariant { id quantityAvailable } } }`;
    const variantGid = `gid://shopify/ProductVariant/${variantId}`;

    const response = await fetch(graphUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({
        query: query,
        variables: {
          id: variantGid,
        },
      }),
    });

    const { data, errors } = await response.json();

    if (errors) {
      console.error('GraphQL errors:', errors);
      return null;
    }

    const variant = data.node;
    if (!variant || variant.__typename !== 'ProductVariant') {
      console.error('Node is not a ProductVariant or does not exist');
      return null;
    }

    return variant.quantityAvailable;
  }

  /**
   * Handles the zero rated submit event for the product form.
   * @param {Event} event - The submit event.
   */
  async handleZeroRatedSubmit(event) {
    if(this.#zeroRatedSwapQuantity === null || this.#zeroRatedSwapQuantity === undefined) {
      return;
    }

    // Get the current submission info
    const form = this.querySelector("form");
    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);
    const currentVariantId = formData.get("id");
    const currentQuantity = Number(formData.get("quantity")) || 1;

    // Get the current cart for up to date quantities
    const currentCartFetchConfig = fetchConfig("javascript");
    let currentCartRequest = await fetch(Theme.routes.cart_url, {
      ...currentCartFetchConfig,
    });
    if (!currentCartRequest.ok) {
      console.error("Unable to update cart", currentCartRequest);
      this.handleCartError(form, formData, { message: "Unable to update cart", description: "Unable to fetch current cart" });
      return;
    }
    const currentCart = await currentCartRequest.json();

    let CustomLIPIndex = 0;

    // Find if the zero rated swap variant is already in the cart
    const zeroRatedSwapVariantInCart = /** @type {Cart} */(currentCart).items.find(
      (item) => item.variant_id === this.#zeroRatedSwapVariant
    );

    // Handle edge cases where users attempt to directly add zero rated swap variants to cart
    // TODO: Consider splitting this out into it's own function? Slotted in the middle as it requires data we already have accessible, however this is getting complex and may benefit from being its own flow
    if(this.#zeroRatedConfig && this.#zeroRatedConfig.taxable === false) {
      // Sum the quantity of the zero rated swap variant the user is attempting to add with the existing quantity of the taxable variant in cart (if applicable)
      const totalVariantQuantity = currentQuantity + (zeroRatedSwapVariantInCart?.quantity || 0);

      // Find the custom index of the current item in the current from LIPs
      let currentVariantIndex = null;
      if(zeroRatedSwapVariantInCart && zeroRatedSwapVariantInCart.properties && zeroRatedSwapVariantInCart.properties.length) {
        zeroRatedSwapVariantInCart.properties.forEach((/** @type {any} */property) => {
          if(property[0] === "_customSortingIndex") {
            currentVariantIndex= parseInt(property[1], 10);
            return;
          }
        });
      }
      if(currentVariantIndex) CustomLIPIndex = Number(currentVariantIndex);
      // Under the threshold to allow the non-taxable variant - add the taxable variant instead
      if(this.#zeroRatedSwapQuantity && totalVariantQuantity <= this.#zeroRatedSwapQuantity) {
        formData.set('id', String(this.#zeroRatedSwapVariant));
        formData.set("properties[_customSortingIndex]", String(CustomLIPIndex));
        this.handleFinalSubmission(form, formData, event, true);
        return;
      }
      else if(totalVariantQuantity > this.#zeroRatedSwapQuantity) {
        // If the taxable variant is already in cart we need to remove it before adding the zero rated variant
        if(zeroRatedSwapVariantInCart) {
          const updateFormData = new FormData();
          updateFormData.append(`updates[${zeroRatedSwapVariantInCart.key}]`, "0");
          const updateFetchConfig = fetchConfig("javascript", { body: updateFormData });

          // Globally scoped routes throw a TS error
          // @ts-ignore
          let updateCartRequest = await fetch(window.Shopify.routes.root + 'cart/update.js', {
            ...updateFetchConfig,
          });
          if (!updateCartRequest.ok) {
            console.error("Unable to update cart", updateCartRequest);
            this.handleCartError(form, formData, { message: "Unable to update cart", description: "Unable to remove existing cart items" });
            return;
          }
        }
        // Final submission to add the zero rated variant. We don't need to modify the 'id' here as the user is already attempting to add the non-taxable variant
        formData.set("quantity", String(totalVariantQuantity));
        formData.set("properties[_customSortingIndex]", String(CustomLIPIndex));
        this.handleFinalSubmission(form, formData, event, true);
        return;
      }
    }
    // End handling for edge case of directly adding zero rated swap variants to cart

    // If the zero rated variant is already in cart we can assume they have already hit the threshold and are adding more of the same variant
    if(zeroRatedSwapVariantInCart && this.#zeroRatedConfig && this.#zeroRatedConfig.taxable) {
      formData.set('id', String(this.#zeroRatedSwapVariant));
      CustomLIPIndex = zeroRatedSwapVariantInCart?.properties?._customSortingIndex;
      formData.set("properties[_customSortingIndex]", String(CustomLIPIndex));
      this.handleFinalSubmission(form, formData, event, true);
      return;
    }

    // Find if the current variant is already in the cart
    const currentVariantInCart = /** @type {Cart} */(currentCart).items.find(
      (item) => item.variant_id === Number(currentVariantId)
    );

    // Sums the quantity of what we're adding with what is associated with the same variant in cart (if applicable)
    const totalVariantQuantity = currentQuantity + (currentVariantInCart?.quantity || 0);

    if(currentVariantInCart) {
      CustomLIPIndex = currentVariantInCart?.properties?._customSortingIndex
    }

    // Find the current LIP sorting index in cart if not set
    if(currentCart.items.length && (CustomLIPIndex === 0 || CustomLIPIndex === null)) {
      currentCart.items.forEach((/** @type {CartItem} */item) => {
        if(item.properties && item.properties["_customSortingIndex"]) {
          const index = parseInt(item.properties["_customSortingIndex"], 10);
          if(index >= CustomLIPIndex) CustomLIPIndex = index + 1;
        }
      });
    }

    // Get the current available stock for the taxable variant
    // If the taxable variant has less stock available than required to hit the NT threshold we prevent the swap below
    const currentVariantStock = await this.getCurrentVariantStock(currentVariantId);

    // If the total quantity being added is under our threshold
    // If the current available variant quantity in Shopify is under the threshold required for the NT swap
    // Add the original product to the cart as usual
    if(this.#zeroRatedSwapQuantity && totalVariantQuantity <= this.#zeroRatedSwapQuantity || !currentVariantStock || this.#zeroRatedSwapQuantity > currentVariantStock) {
      formData.set("properties[_customSortingIndex]", String(CustomLIPIndex));
      this.handleFinalSubmission(form, formData, event, true);
      return;
    }

    // If the total quantity exceeds our threshold we need to remove the existing item from the cart
    // When then proceed to add the zero rated swap variant to the cart with the previous summed quantity
    // This has to explicitly check for `null` values for the swap instead of a loose check because we have a unique threshold of 0 that needs to fall into this flow.
    if(this.#zeroRatedSwapQuantity !== null && totalVariantQuantity > this.#zeroRatedSwapQuantity) {
      if(currentVariantInCart) {
        // Find the custom index of the current item in the current from LIPs
        let currentVariantIndex = null;
        if(currentVariantInCart.properties && currentVariantInCart.properties.length) {
          currentVariantInCart.properties.forEach((/** @type {any} */property) => {
            if(property[0] === "_customSortingIndex") {
              currentVariantIndex= parseInt(property[1], 10);
              return;
            }
          });
        }
        if(currentVariantIndex) CustomLIPIndex = Number(currentVariantIndex);
        // Remove the current cart item
        const updateFormData = new FormData();
        updateFormData.append(`updates[${currentVariantInCart.key}]`, "0");
        const updateFetchConfig = fetchConfig("javascript", { body: updateFormData });

        // Globally scoped routes throw a TS error
        // @ts-ignore
        let updateCartRequest = await fetch(window.Shopify.routes.root + 'cart/update.js', {
          ...updateFetchConfig,
        });
        if (!updateCartRequest.ok) {
          console.error("Unable to update cart", updateCartRequest);
          this.handleCartError(form, formData, { message: "Unable to update cart", description: "Unable to remove existing cart items" });
          return;
        }
      }

      // The final add to cart event which adds the total summed quantity with the new zero rated variant ID
      formData.set('id', String(this.#zeroRatedSwapVariant));
      const finalVariantQuantity = totalVariantQuantity <= currentVariantStock ? totalVariantQuantity : currentVariantStock;
      formData.set("quantity", String(finalVariantQuantity));
      formData.set("properties[_customSortingIndex]", String(CustomLIPIndex));
      this.handleFinalSubmission(form, formData, event, true);
      return;
    }

    // We should never get here, this probably indicates an error in the zero rated config
    console.error("No paths met for zero rated product configuration. Check product data.");
    console.log("Config data: ", this.#zeroRatedConfig);
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute(
        "disabled"
      ) === "true"
    )
      return;

    this.refs.addToCartButtonContainer?.refs.addToCartButton?.setAttribute("disabled", '');

    // Double check our config for sanity and lazy updates
    this.#setZeroRateConfig();

    if (this.#zeroRatedSupport) {
      this.handleZeroRatedSubmit(event);
      return;
    }

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);

    this.handleFinalSubmission(form, formData, event);
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector(
      '[ref="addToCartButton"]'
    );

    if (!currentAddToCartButton) return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        "hidden",
        "true"
      );
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the currently selected variant ID associated with the add to cart button so we can re-run our custom stock validation logic against the new variant
    addToCartButtonContainer.setAttribute("data-current-variant", event.detail.resource.id || "");
    // Reset the has touched stock flag as the flow above has assigned new language and state that we need to respect if possible
    addToCartButtonContainer.hasTouchedStock = false;
    // Revalidate custom stock logic against the new variant
    addToCartButtonContainer.validateStock();

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? "";

    this.#setZeroRateConfig();

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100"
        );
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = "1";

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(
        progress,
        startPoint,
        controlPoint1,
        controlPoint2,
        endPoint
      );

      //Update the position of the flying thingy
      this.style.setProperty("--x", `${position.x}px`);
      this.style.setProperty("--y", `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty("--scale", `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = "0";
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty("--x", `${startPoint.x}px`);
    this.style.setProperty("--y", `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
